import { StageLocations } from '@/routes/projectStages/issue/types/issue.ts'
import { PopoverProperty } from '@/components/common/propertyPopoverOverlay/Property.tsx'
import createSeparator from '@/components/common/propertyPopoverOverlay/createSeparator.tsx'
import { getAllNestedLocationsIds } from '@/routes/projectStages/issues/lib/issuesHelpers/locationFiltersHelpers.ts'
import { useQuery } from '@tanstack/react-query'
import { stageLocationsQuery } from '@/routes/projectStages/issue/actions.ts'
import { sortLocations } from '@/lib/helpers/locationOrder.ts'

export default function useCreateLocationsFilters(
  stageId: string | null | undefined | number,
  selectAllProperty: PopoverProperty,
) {
  const { data: stageLocations } = useQuery({
    ...stageLocationsQuery(String(stageId)),
    enabled: !!stageId,
  })

  const locations: PopoverProperty[] = transformLocations(stageLocations)

  function transformLocations(locations: StageLocations | null | undefined): PopoverProperty[] {
    if (!locations) {
      return []
    }

    // A-to-Z at every level of the tree. Sorting the SOURCE rather than the mapped
    // properties keeps the injected "select all" row and its separator pinned to the
    // top of each children list, where they belong, instead of being sorted into it.
    return sortLocations(locations)
      .map(location => {
        let children: PopoverProperty[] = []

        if (location.children) {
          children = transformLocations(location.children)
        }

        const childrenIds = getAllNestedLocationsIds(children)
        const ids = [...childrenIds, location.id]

        const idsString = ids.join('.')

        const selectAllWithChildren: PopoverProperty = {
          ...selectAllProperty,
          value: `all-locations-${idsString}`,
        }

        return {
          label: location.name ?? '',
          searchableValue: location.name ?? '',
          value: location['@id'],
          children:
            children.length > 0
              ? [selectAllWithChildren, createSeparator(), ...children]
              : undefined,

          parent: 'locations',
        }
      })
      .filter(Boolean) as PopoverProperty[]
  }

  return {
    locations,
  }
}
